import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type {
  ApiClient, Projekt, Elektroverteiler, Elektroverteilerplandaten,
  Elektroverteilernachgelagert,
} from '../api';
import { IconElektro, IconWarnung, IconHerunterladen, IconSuche } from '../Icons';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ContentSection, DataTable, EmptyState, ErrorState, KpiCard, KpiReihe,
  LoadingState, StatusBadge, type Spalte,
} from '../muster';

interface Rahmen { api: ApiClient; projekt: Projekt | null }

/**
 * Verteilerplanung: Bestückung, Stromkreise und Kennzahlen je Verteiler
 * (Paket VT3), mit Abrufknopf für den Bericht ELT-VTP.
 *
 * --- Warum eine eigene Seite und nicht ein Reiter an "Verteiler und
 *     Leistungsbilanz" -----------------------------------------------------
 *
 * "Verteiler und Leistungsbilanz" (elektro-verteiler) pflegt die
 * Verteilerstammdaten von Hand — Bemessungsgrößen, Netzform, Schrank. Diese
 * Seite zeigt das GEGENTEIL: was die Verteilerbestückung (Paket VT1)
 * AUTOMATISCH aus dem Bestand gebildet hat, nach dem Muster mustereta.pdf.
 * Beides zu vermischen hieße, eine gepflegte Angabe neben einer errechneten
 * so darzustellen, als wären sie derselben Herkunft.
 *
 * --- Muster: Baum links, Inhalt rechts (wie Heizung/Kälte) ----------------
 *
 * Dieselben Klassen wie überall im Gewerk (`baum`, `baum-ebene`,
 * `baum-zeile`, `tabellenrahmen`, `kennzahl*`) — keine neuen Maße.
 */
export function ElektroVerteilerplanung() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [liste, setListe] = useState<Elektroverteiler[] | null>(null);
  const [gewaehlt, setGewaehlt] = useState<Elektroverteiler | null>(null);
  const [suche, setSuche] = useState('');
  const [offen, setOffen] = useState<Set<string>>(new Set());
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    if (!projekt) return;

    (async () => {
      try {
        const l = await api.elektroVerteiler(projekt.id);
        // `?? []` und nicht `l.verteiler`: Eine Antwort ohne `verteiler` liess
        // die Seite ewig im Ladezustand stehen, statt ihren Leerzustand zu
        // zeigen (gemessen im Pruefstand, Paket U6b).
        setListe(l.verteiler ?? []);
        setFehler(null);
      } catch (e) {
        setFehler(e instanceof Error ? e.message : String(e));
      }
    })();
  }, [projekt?.id]);

  const gefiltert = useMemo(() => {
    if (!liste) return null;

    const s = suche.trim().toLowerCase();
    const treffer = s === ''
      ? liste
      : liste.filter((v) => v.kennzeichen.toLowerCase().includes(s)
        || (v.bezeichnung ?? '').toLowerCase().includes(s));

    const gruppen = new Map<string, Elektroverteiler[]>();

    for (const v of treffer) {
      const schluessel = v.art;
      if (!gruppen.has(schluessel)) gruppen.set(schluessel, []);
      gruppen.get(schluessel)!.push(v);
    }

    return [...gruppen.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([art, kinder]) => ({
        art,
        kinder: kinder.sort((a, b) => a.kennzeichen.localeCompare(b.kennzeichen)),
      }));
  }, [liste, suche]);

  const kopf = (
    <PageHeader
      titel="Elektro — Verteilerplanung"
      beschreibung={'Bestückung, Stromkreise und Kennzahlen je Verteiler, wie die '
        + 'Verteilerbestückung sie aus dem Bestand gebildet hat. Die von Hand '
        + 'gepflegten Stammdaten stehen auf „Verteiler und Leistungsbilanz“ — '
        + 'gerechnet und gepflegt werden hier bewusst nicht vermischt.'}
      meta={liste ? `${liste.length} Verteiler` : undefined}
    />
  );

  if (fehler) {
    return (
      <>
        {kopf}
        <ErrorState
          meldung="Die Verteiler konnten nicht geladen werden."
          technisch={fehler}
        />
      </>
    );
  }

  if (!projekt || !liste) {
    return (
      <>
        {kopf}
        <LoadingState text="Verteiler werden geladen …" zeilen={8} spalten={3} />
      </>
    );
  }

  if (liste.length === 0) {
    return (
      <>
        {kopf}
        <EmptyState
          titel="Für dieses Modell ist noch kein Verteiler aufgenommen."
          erklaerung={'Die Verteiler entstehen aus der Modellaufnahme des Gewerks '
            + 'Elektro; ihre Bestückung bildet danach das Werkzeug '
            + '„verteiler-bestuecken“.'}
        />
      </>
    );
  }

  return (
    <>
    {kopf}
    <div className="karte" style={{ display: 'flex', gap: 0, padding: 0 }}>
      {/* Links die Verteiler nach Art, rechts der gewählte Verteiler. */}
      <div className="baum">
        <div style={{ padding: '10px 12px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <IconSuche size={14} />
            <input
              value={suche}
              onChange={(e) => setSuche(e.target.value)}
              placeholder="Kennzeichen oder Bezeichnung"
              style={{ width: '100%', padding: '4px 6px', fontSize: 13 }}
            />
          </label>
        </div>

        {(gefiltert ?? []).map((g) => (
          <div key={g.art}>
            <button
              className="baum-ebene"
              onClick={() => setOffen((s) => {
                const neu = new Set(s);
                if (neu.has(g.art)) neu.delete(g.art); else neu.add(g.art);
                return neu;
              })}
            >
              <IconElektro size={14} />
              <span>{g.art}</span>
              <span className="schwach">{g.kinder.length}</span>
            </button>

            {(offen.has(g.art) || suche.trim() !== '') && g.kinder.map((v) => (
              <button
                key={v.id}
                className={`baum-zeile ${gewaehlt?.id === v.id ? 'aktiv' : ''}`}
                onClick={() => setGewaehlt(v)}
                title={v.bezeichnung ?? v.kennzeichen}
              >
                <span style={{ minWidth: 0, overflow: 'hidden',
                               textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {v.kennzeichen}
                  {v.bezeichnung && (
                    <span className="schwach" style={{ marginLeft: 5 }}>
                      {v.bezeichnung}
                    </span>
                  )}
                </span>

                {/* --- Die Bilanzlücke, schon im Baum -------------------

                    Ohne sie muss man jeden Verteiler anklicken, um zu
                    sehen, ob seine Bilanz fehlt — und eine fehlende Bilanz
                    sah ohnehin aus wie eine mit dem Ergebnis null.

                    `warnung` und nicht `fehler`: ein offener Arbeitspunkt,
                    kein Mangel (Auftrag 17.09.2026, Abschnitt 18). */}
                {v.bilanzKette?.startsWith('LUECKE: ') && (
                  <StatusBadge
                    zustand="warnung"
                    text="Lücke"
                    titel={v.bilanzKette.slice('LUECKE: '.length)}
                    klein
                  />
                )}
              </button>
            ))}
          </div>
        ))}
      </div>

      <div style={{ flex: 1, padding: '14px 16px', minWidth: 0,
                    maxHeight: '70vh', overflowY: 'auto' }}>
        {!gewaehlt ? (
          <EmptyState
            klein
            titel="Noch kein Verteiler gewählt."
            erklaerung="Links eine Verteilerart aufklappen und einen Verteiler wählen — dann stehen hier Bestückung, Stromkreise und Kennzahlen."
          />
        ) : (
          <VerteilerInhalt
            api={api}
            projekt={projekt}
            verteiler={gewaehlt}
            waehle={(id) => {
              // Ein Abgang an einer Einspeiseebene muss anklickbar sein,
              // sonst endet der Weg dort. Der Baum wird dabei an der Art
              // des Ziels aufgeklappt - sonst springt die Auswahl auf einen
              // Knoten, den man nicht sieht.
              const ziel = liste.find((v) => v.id === id);
              if (!ziel) return;
              setOffen((alt2) => new Set(alt2).add(ziel.art));
              setGewaehlt(ziel);
            }}
          />
        )}
      </div>
    </div>
    </>
  );
}

function VerteilerInhalt({
  api, projekt, verteiler, waehle,
}: {
  api: ApiClient; projekt: Projekt; verteiler: Elektroverteiler;
  waehle: (id: string) => void;
}) {
  const [daten, setDaten] = useState<Elektroverteilerplandaten | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [ladeBericht, setLadeBericht] = useState(false);

  useEffect(() => {
    setDaten(null);
    setFehler(null);
    setLaedt(true);

    (async () => {
      try {
        setDaten(await api.elektroVerteilerplanbestueckung(verteiler.id));
      } catch (e) {
        setFehler(e instanceof Error ? e.message : String(e));
      } finally {
        setLaedt(false);
      }
    })();
  }, [verteiler.id]);

  const berichtLaden = async () => {
    setLadeBericht(true);
    try {
      await api.elektroVerteilerplanberichtLaden(verteiler.id, verteiler.kennzeichen);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLadeBericht(false);
    }
  };

  const schemaLaden = async () => {
    setLadeBericht(true);
    try {
      await api.elektroMittelspannungsschemaLaden(projekt.id);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLadeBericht(false);
    }
  };

  // --- Einspeiseebene: was es hier gibt und was es nicht geben kann -------
  //
  // Einen Verteiler-Stromlaufplan (ELT-VTP) kann es an ms, trafo und nshv
  // NICHT geben: Er zeichnet Abgangsschaltungen aus der Bestueckung, und die
  // gibt es dort nicht. Es gibt aber das Mittelspannungsschema (Paket EG2),
  // das genau diese Ebene zeigt. Ein Knopf, der einen Fehler erzeugt, ist
  // schlechter als kein Knopf — deshalb steht hier der, den es gibt.
  const einspeiseebene = ['ms', 'trafo', 'nshv', 'einspeisung']
    .includes(verteiler.art);

  const leergrund = daten?.leergrund ?? null;

  const leerTitel = leergrund === 'einspeiseebene'
    ? 'An dieser Einspeiseebene hängt planmäßig keine Bestückung.'
    : leergrund === 'it'
      ? 'Dieser IT-Verteiler wird nicht über die Verteilerbestückung geführt.'
      : 'Für diesen Verteiler ist noch keine Bestückung gebildet.';

  const nachgelagertspalten: Spalte<Elektroverteilernachgelagert>[] = [
    {
      schluessel: 'kennzeichen', titel: 'Kennzeichen', immerSichtbar: true,
      wert: (n) => n.kennzeichen,
      inhalt: (n) => (
        <button
          className="knopf-mager"
          onClick={() => waehle(n.id)}
          title={`${n.kennzeichen} öffnen`}
        >
          {n.kennzeichen}
        </button>
      ),
    },
    { schluessel: 'bezeichnung', titel: 'Bezeichnung', wert: (n) => n.bezeichnung },
    { schluessel: 'art', titel: 'Art', wert: (n) => n.art },
    { schluessel: 'raum', titel: 'Raum', wert: (n) => n.raumnummer },
    { schluessel: 'geschoss', titel: 'Geschoss', wert: (n) => n.geschoss },
  ];

  // Kennzeichen sind in der Bestückung nicht zwingend eindeutig (der Bestand
  // benutzte deshalb `${kennzeichen}-${i}` als Reactschlüssel). `DataTable`
  // verlangt einen stabilen Schlüssel je Zeile, also bekommt jede Zeile hier
  // ihre laufende Nummer mit.
  const bestueckung = (daten?.bestueckung ?? []).map((z, i) => ({ ...z, _nr: i }));
  const kreise = (daten?.kreise ?? []).map((k, i) => ({ ...k, _nr: i }));

  const bestueckungsspalten: Spalte<any>[] = [
    { schluessel: 'kennzeichen', titel: 'Kennzeichen', immerSichtbar: true, wert: (z) => z.kennzeichen },
    { schluessel: 'art', titel: 'Art', wert: (z) => z.art },
    { schluessel: 'bezeichnung', titel: 'Bezeichnung', wert: (z) => z.bezeichnung },
    {
      schluessel: 'in', titel: 'Bemessungsstrom', art: 'zahl', einheit: 'A',
      wert: (z) => z.bemessungsstromA,
    },
    { schluessel: 'pole', titel: 'Pole', art: 'zahl', wert: (z) => z.polzahl },
    { schluessel: 'verbraucher', titel: 'Verbraucher', wert: (z) => z.verbraucher },
    {
      schluessel: 'auslastung', titel: 'Auslastung', art: 'zahl', einheit: '%',
      nachkomma: 1, wert: (z) => z.auslastungProzent,
      inhalt: (z) => (z.auslastungProzent == null
        ? <span className="schwach">—</span>
        : z.auslastungProzent > 80
          ? (
            <StatusBadge
              zustand="warnung"
              text={`${z.auslastungProzent.toLocaleString('de-DE', { maximumFractionDigits: 1 })} % — über 80 %`}
              klein
            />
          )
          : <>{z.auslastungProzent.toLocaleString('de-DE', { maximumFractionDigits: 1 })}</>),
    },
    { schluessel: 'raum', titel: 'Raum', wert: (z) => z.raumnummer },
  ];

  const kreisspalten: Spalte<any>[] = [
    { schluessel: 'kennzeichen', titel: 'Kennzeichen', immerSichtbar: true, wert: (k) => k.kennzeichen },
    { schluessel: 'bezeichnung', titel: 'Bezeichnung', wert: (k) => k.bezeichnung },
    { schluessel: 'verwendung', titel: 'Verwendung', wert: (k) => k.verwendung },
    { schluessel: 'schutzorgan', titel: 'Schutzorgan', wert: (k) => k.schutzorgan },
    {
      schluessel: 'in', titel: 'Bemessungsstrom', art: 'zahl', einheit: 'A',
      wert: (k) => k.bemessungsstromA,
    },
    {
      schluessel: 'querschnitt', titel: 'Querschnitt', art: 'zahl', einheit: 'mm²',
      wert: (k) => k.querschnitt,
    },
    { schluessel: 'raum', titel: 'Raum', wert: (k) => k.raumnummer },
  ];

  return (
    <>
      <h2 className="karte-titel" style={{ marginTop: 0 }}>
        <IconElektro />{verteiler.kennzeichen}
        {verteiler.bezeichnung && (
          <span className="schwach">{verteiler.bezeichnung}</span>
        )}
      </h2>

      {fehler && (
        <ErrorState
          klein
          meldung="Die Bestückung dieses Verteilers konnte nicht geladen werden."
          technisch={fehler}
        />
      )}

      {laedt && <LoadingState text="Bestückung wird geladen …" zeilen={6} spalten={4} />}

      {!laedt && !fehler && !daten && (
        <EmptyState
          titel="Für diesen Verteiler liegt noch keine Bestückung vor."
          erklaerung={'Die Bestückung entsteht aus dem Werkzeug „verteiler-bestuecken“ '
            + '— für diesen Verteiler ist es noch nicht gelaufen, oder er hat keine '
            + 'zugeordneten Bauteile.'}
        />
      )}

      {daten && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10,
                        marginBottom: 12, flexWrap: 'wrap' }}>
            {/* Ohne Bestückung gäbe der ELT-VTP ein Blatt mit leeren
                Tabellen — der Endpunkt lehnt ihn deshalb ab. Den Knopf hier
                anzubieten hieße, einen Fehler zum Anklicken auszulegen. */}
            {!leergrund && (
              <button onClick={berichtLaden} disabled={ladeBericht}
                      className="betont">
                <IconHerunterladen size={14} /> Verteilerplan (ELT-VTP)
              </button>
            )}

            {einspeiseebene && (
              <button onClick={schemaLaden} disabled={ladeBericht}
                      className={leergrund ? 'betont' : undefined}
                      title="MS-Anlagen, Kupplung, Trafoabzweige und die NSHV darunter">
                <IconHerunterladen size={14} /> Mittelspannungsschema
              </button>
            )}

            {!leergrund && (
              <span className="sehr-schwach" style={{ fontSize: 11 }}>
                Fassung {daten.fassung}
                {daten.berechnetAm && (
                  <> · {new Date(daten.berechnetAm).toLocaleString('de-DE')}</>
                )}
              </span>
            )}

            {!leergrund && !daten.stromlaufplanVorhanden && (
              <span className="marke warnung" style={{ fontSize: 10 }}>
                ohne Stromlaufplan
              </span>
            )}
          </div>

          {leergrund ? (
            <>
              {/* --- Leer ist eine Antwort, kein Fehler ------------------

                  Hier stand bis zum 19.09.2026 ein ErrorState mit
                  „HTTP 404". Er traf 48 Verteiler und 12 Unterverteiler —
                  und an einer Einspeiseebene war die leere Bestückung
                  sogar die richtige Aussage. */}
              <EmptyState
                titel={leerTitel}
                erklaerung={daten.leergrundText ?? undefined}
              />

              {daten.nachgelagerte.length > 0 && (
                <ContentSection
                  titel={`Nachgelagerte Verteiler (${daten.nachgelagerte.length})`}
                  nackt
                >
                  <DataTable
                    name="Nachgelagerte Verteiler"
                    spalten={nachgelagertspalten}
                    zeilen={daten.nachgelagerte}
                    schluessel={(n) => n.id}
                    dicht
                    ausfuhr
                    ausfuhrDateiname={`abgaenge-${verteiler.kennzeichen}`}
                  />
                </ContentSection>
              )}
            </>
          ) : (
          <>
          <KpiReihe>
            {/* --- Die Leistungsbilanz dieses Verteilers -----------------

                Drei Zustände, und zwei davon waren bisher nicht zu
                unterscheiden: „null Last" und „keine Bilanz, weil die
                Kette darüber fehlt". Am 18.09.2026 traf das alle elf
                Transformatoren — sie standen ohne Bedarf da, obwohl alle
                elf eine Bemessungsleistung tragen. */}
            {verteiler.bilanzKette?.startsWith('LUECKE: ') ? (
              <KpiCard
                titel="Leistungsbilanz"
                wert="Lücke"
                zustand="warnung"
                zustandText="Kette unterbrochen"
                fussnote={verteiler.bilanzKette.slice('LUECKE: '.length)}
              />
            ) : verteiler.bedarfsleistung != null ? (
              <KpiCard
                titel="Bedarfsleistung"
                wert={verteiler.bedarfsleistung / 1000}
                einheit="kW" nachkomma={1}
                fussnote={verteiler.bilanzKette ?? undefined}
              />
            ) : (
              <KpiCard
                titel="Leistungsbilanz"
                wert="—"
                zustand="neutral"
                zustandText="nicht gerechnet"
                fussnote={'Für diesen Verteiler ist keine Bilanz gerechnet. Das '
                  + 'ist nicht dasselbe wie null Last — „Bilanz prüfen" auf '
                  + '„Verteiler und Leistungsbilanz" nennt den Grund.'}
              />
            )}

            <KpiCard titel="Betriebsmittel" wert={daten.betriebsmittelGesamt} />
            <KpiCard titel="Stromkreise" wert={daten.kreiseGebildet} />
            <KpiCard titel="Verbraucher" wert={daten.verbraucherGesamt} />
            <KpiCard
              titel="höchste Auslastung" wert={daten.hoechsteAuslastungProzent}
              einheit="%" nachkomma={1}
              zustand={daten.hoechsteAuslastungProzent > 80 ? 'warnung' : 'gut'}
              zustandText={daten.hoechsteAuslastungProzent > 80
                ? 'über 80 %' : 'im Rahmen'}
            />
            <KpiCard
              titel="Sicherungen über 80 %" wert={daten.sicherungenUeber80}
              zustand={daten.sicherungenUeber80 > 0 ? 'warnung' : 'gut'}
              zustandText={daten.sicherungenUeber80 > 0 ? 'nachsehen' : 'keine'}
            />
            <KpiCard
              titel="Reserveplätze" wert={daten.reserveplaetze}
              vergleich={`${daten.reserveanteilProzent.toLocaleString('de-DE', { maximumFractionDigits: 1 })} % der Plätze`}
            />
          </KpiReihe>

          {daten.hinweise.length > 0 && (
            <div className="hinweis" style={{ display: 'block', marginTop: 8 }}>
              {daten.hinweise.map((h, i) => (
                <div key={i} style={{ fontSize: 12 }}>
                  <IconWarnung size={12} /> {h}
                </div>
              ))}
            </div>
          )}

          <ContentSection titel="Bestückung" nackt>
            <DataTable
              name="Bestückung"
              spalten={bestueckungsspalten}
              zeilen={bestueckung}
              schluessel={(z) => `${z.kennzeichen}-${z._nr}`}
              dicht
              ausfuhr
              ausfuhrDateiname={`bestueckung-${verteiler.kennzeichen}`}
              leer={(
                <EmptyState
                  titel="Dieser Verteiler hat kein Betriebsmittel."
                  erklaerung="Die Bestückung entsteht aus den dem Verteiler zugeordneten Bauteilen des Modells."
                />
              )}
            />
          </ContentSection>

          <ContentSection titel="Stromkreise" nackt>
            <DataTable
              name="Stromkreise"
              spalten={kreisspalten}
              zeilen={kreise}
              schluessel={(k) => `${k.kennzeichen}-${k._nr}`}
              dicht
              ausfuhr
              ausfuhrDateiname={`stromkreise-${verteiler.kennzeichen}`}
              leer={(
                <EmptyState
                  titel="Für diesen Verteiler ist kein Stromkreis gebildet."
                  erklaerung="Stromkreise entstehen aus der Verteilerbestückung; ohne zugeordnete Verbraucher bleibt die Liste leer."
                />
              )}
            />
          </ContentSection>
          </>
          )}
        </>
      )}

      <VerteilerZeichnungen api={api} projekt={projekt} verteiler={verteiler} />
    </>
  );
}

/**
 * ED-2/ED-3 (16.10.2026): Einlinienschema und Schrankansicht des Verteilers —
 * DIESELBE Zeichnung wie im Verteilerplan-PDF (SVG vom Server, eine
 * Bibliothek der Schaltzeichen). Bis v0.50.12 gab es sie nur im PDF.
 */
function VerteilerZeichnungen({
  api, projekt, verteiler,
}: {
  api: ApiClient; projekt: Projekt; verteiler: Elektroverteiler;
}) {
  const [art, setArt] = useState<
    'einlinienschema' | 'schrankansicht' | 'netzschema' | 'lageplan' | 'installationsplan'>('einlinienschema');
  const [blatt, setBlatt] = useState(1);
  const [bild, setBild] = useState<{ url: string; blaetter: number } | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedtPdf, setLaedtPdf] = useState(false);

  useEffect(() => { setBlatt(1); }, [verteiler.id, art]);

  useEffect(() => {
    let url: string | null = null;
    let aktiv = true;
    setBild(null);
    setFehler(null);

    (async () => {
      try {
        const v = await api.elektroVerteilerZeichnungSvg(
          verteiler.id, art, blatt, projekt.id, verteiler.geschoss);
        url = v.url;
        if (aktiv) setBild({ url: v.url, blaetter: v.blaetter });
        else URL.revokeObjectURL(v.url);
      } catch (e) {
        if (aktiv) setFehler(e instanceof Error ? e.message : String(e));
      }
    })();

    return () => {
      aktiv = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [verteiler.id, verteiler.geschoss, projekt.id, art, blatt]);

  const titel = art === 'einlinienschema' ? 'Einlinienschema'
    : art === 'schrankansicht' ? 'Schrankansicht'
      : art === 'lageplan' ? 'Verteiler-Lageplan'
        : art === 'installationsplan' ? 'Installationsplan' : 'Netzschema';

  return (
    <ContentSection
      titel={art === 'netzschema' ? 'Netzschema des Projekts'
        : art === 'lageplan' || art === 'installationsplan' ? `${titel} ${verteiler.geschoss ?? ''}`.trim()
          : `${titel} ${verteiler.kennzeichen}`}
      beschreibung={art === 'einlinienschema'
        ? 'Einpolige Darstellung nach DIN EN 61082-1 mit Schaltzeichen nach den Grundformen '
          + 'der DIN EN 60617: Einspeisung, Hauptschalter, Schiene, Vorsicherungen, RCD-Gruppen '
          + 'und Endabgänge mit Kabel und Ziel. „!“ = Angabe fehlt.'
        : art === 'schrankansicht'
          ? 'Frontansicht im Maßstab 1:5, Teilungsraster 18 mm (Gerätebreite 17,5 mm nach '
            + 'DIN 43880), freie Plätze schraffiert. Kein Herstellerraster.'
          : art === 'lageplan'
            ? 'Lage der Verteiler im Geschoss dieses Verteilers, auf dem Grundriss des Aussparungsplans.'
            : art === 'installationsplan'
              ? 'Leuchten, Steckdosen, Schalter, Melder, Dosen und Auslässe im Geschoss dieses Verteilers, '
                + 'je Quadrant ein Blatt. Blaue Kreise sind Kennzeichen, bis der Normtext DIN EN 60617-11 vorliegt.'
            : 'Blatt 1: MS-Anlagen, Transformatoren und Trafogruppen mit ihren NSHV; danach je NSHV '
              + 'und je SV-Anlage das Einlinienschema der Abgänge.'}
      aktionen={(
        <>
          <button
            className={art === 'einlinienschema' ? 'knopf' : 'knopf-mager'}
            onClick={() => setArt('einlinienschema')}
            style={{ fontSize: 12 }}
          >
            Einlinienschema
          </button>
          <button
            className={art === 'schrankansicht' ? 'knopf' : 'knopf-mager'}
            onClick={() => setArt('schrankansicht')}
            style={{ fontSize: 12 }}
          >
            Schrankansicht
          </button>
          <button
            className={art === 'netzschema' ? 'knopf' : 'knopf-mager'}
            onClick={() => setArt('netzschema')}
            style={{ fontSize: 12 }}
          >
            Netzschema
          </button>
          <button
            className={art === 'lageplan' ? 'knopf' : 'knopf-mager'}
            onClick={() => setArt('lageplan')}
            style={{ fontSize: 12 }}
          >
            Lageplan
          </button>
          <button
            className={art === 'installationsplan' ? 'knopf' : 'knopf-mager'}
            onClick={() => setArt('installationsplan')}
            style={{ fontSize: 12 }}
          >
            Installationsplan
          </button>
          {bild && bild.blaetter > 1 && (
            <>
              <button className="knopf-mager" disabled={blatt <= 1}
                      onClick={() => setBlatt((b) => Math.max(1, b - 1))} style={{ fontSize: 12 }}>
                ‹ Blatt
              </button>
              <span className="schwach" style={{ fontSize: 12 }}>
                Blatt {blatt} von {bild.blaetter}
              </span>
              <button className="knopf-mager" disabled={blatt >= bild.blaetter}
                      onClick={() => setBlatt((b) => Math.min(bild.blaetter, b + 1))} style={{ fontSize: 12 }}>
                Blatt ›
              </button>
            </>
          )}
          <button
            className="knopf-mager"
            disabled={laedtPdf}
            style={{ fontSize: 12 }}
            onClick={async () => {
              setLaedtPdf(true);
              try {
                if (art === 'netzschema') await api.elektroNetzschemaLaden(projekt.id);
                else if (art === 'lageplan') await api.elektroVerteilerLageplanLaden(projekt.id);
                else if (art === 'installationsplan') {
                  await api.elektroInstallationsplanLaden(projekt.id, verteiler.geschoss);
                }
                else await api.elektroVerteilerplanLaden(verteiler.id, verteiler.kennzeichen);
              } catch (e) {
                setFehler(e instanceof Error ? e.message : String(e));
              } finally {
                setLaedtPdf(false);
              }
            }}
          >
            <IconHerunterladen size={13} /> {art === 'netzschema' ? 'Netzschema (PDF)'
              : art === 'lageplan' ? 'Lageplan (PDF)'
                : art === 'installationsplan' ? 'Installationsplan (PDF)' : 'Verteilerplan (PDF)'}
          </button>
        </>
      )}
    >
      {fehler ? (
        <ErrorState klein meldung={`${titel} ließ sich nicht zeichnen.`} technisch={fehler} />
      ) : !bild ? (
        <LoadingState text={`${titel} wird gezeichnet …`} />
      ) : (
        <img
          src={bild.url}
          alt={`${titel} ${verteiler.kennzeichen}, Blatt ${blatt}`}
          style={{ width: '100%', maxWidth: 1400, border: '1px solid var(--linie)', background: '#fff' }}
        />
      )}
    </ContentSection>
  );
}
